import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, CardBody, Input, Button, Label, Spinner } from 'reactstrap';
import Header from '../../component/layout/Header';
import Footer from '../../component/layout/Footer';
import Page from '../../component/layout/Page';
import Title from '../../component/layout/Title';
import axios from 'axios';
import { mainurl } from '../../MainUrl';
import { notification } from '../../component/hocs/notification';

const MachineStockOut = () => {

  const [formData, setFormData] = useState({
    finished_product_name: '',
    stock_out: '',
    uom_id: '',
    machine_id: '',
    transaction_date: new Date().toISOString().split('T')[0], // Initial date value
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [productList, setProductList] = useState([]);
  const [machineList, setMachineList] = useState([]);

  const [uomOptions] = useState([
    { id: 1, name: 'Kilogram' },
    { id: 2, name: 'Gram' },
    { id: 3, name: 'Piece' },
    { id: 4, name: 'Liter' },
    { id: 5, name: 'Milliliter' },
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchProductList();
    fetchMachineList();
  }, []);

  // Fetch Finished Product List
  const fetchProductList = async () => {
    try {
      const response = await axios.get(mainurl + 'product_list');
      if (Array.isArray(response.data.data)) {
        setProductList(response.data.data);
      } else {
        console.error('Invalid product data structure');
      }
    } catch (err) {
      console.error('Error fetching finished product list:', err);
      notification({ type: 'error', message: 'Error while fetching finished product list', position: 'bottom' });
    }
  };

  const fetchMachineList = async () => {
    try {
      const response = await axios.get(mainurl + 'machine_list');
      if (Array.isArray(response.data.data)) {
        setMachineList(response.data.data);
      }
    } catch (err) {
      notification({ type: 'error', message: 'Error fetching machine list', position: 'bottom' });
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    // List of required fields
    const requiredFields = ['finished_product_name', 'machine_id', 'uom_id', 'transaction_date', 'stock_out'];

    // Array to hold missing fields
    let missingFields = [];

    // Check if all required fields are populated
    requiredFields.forEach(field => {
      if (!formData[field] || formData[field] === '') {
        missingFields.push(field);
      }
    });

    // If any fields are missing, log them and show an error
    if (missingFields.length > 0) {
      console.log('Missing fields:', missingFields);  // Log missing fields to the console
      setError(`Missing required fields: ${missingFields.join(', ')}`);  // Set error with missing fields
      notification({ type: 'error', message: `Please fill in the following fields: ${missingFields.join(', ')}`, position: 'bottom' });
      setLoading(false);
      return; // Stop the submission
    }

    // Log the stock_out value to check the actual value
    console.log('Stock Out Value:', formData.stock_out);
  
    if (formData.stock_out === '' || isNaN(formData.stock_out) || formData.stock_out <= 0) {
      // Log the error and display the message
      console.error('Error: Stock out must be a valid number greater than 0');
      setError('Stock out must be a valid number greater than 0');
      notification({ type: 'error', message: 'Please enter a valid stock out value', position: 'bottom' });
      setLoading(false);
      return;
    }
    
    // Call the API
    try {
      const response = await axios.post(`${mainurl}machine_out`, formData);
      if (response.status === 200) {
        notification({ type: 'success', message: 'Stock Out Successful!', position: 'bottom' });
        setFormData({
          finished_product_name: '',
          stock_out: '',
          uom_id: '',
          machine_id: '',
          transaction_date: new Date().toISOString().split('T')[0],
        });
      } else {
        setError('Failed to create');
        notification({ type: 'error', message: 'Failed to save product', position: 'bottom' });
      }
    } catch (err) {
      console.error('Error:', err.response ? err.response.data : err);
      setError('Failed to create');
      notification({ type: 'error', message: 'Error while saving product', position: 'bottom' });
    } finally {
      setLoading(false);
    }
  };

  // Reset form fields
  const resetForm = () => {
    setFormData({
      finished_product_name: '',
      stock_out: '',
      uom_id: '',
      machine_id: '',
      transaction_date: new Date().toISOString().split('T')[0], // Reset to current date
    });
  };

  return (
    <div>
      <Header />
      <Page>
        <Row className="mb-3">
          <Col xs={12} className="mb-3">
            <Title pageTitle="Machine Stock Out" />
          </Col>
          <Col xs={12}>
            <Card color="primary" outline className="mb-3">
              <CardBody>
                <Row className="my-2">
                  {/* Finished Product Name */}
                  <Col md={6}>
                    <Label>Finished Product Name</Label>
                    <Input
                      type="select"
                      value={formData.finished_product_name}
                      onChange={(e) => setFormData({ ...formData, finished_product_name: e.target.value })}
                      required
                    >
                      <option value="">Select Finished Product</option>
                      {productList.map((product) => (
                        <option key={product.finished_product_id} value={product.finished_product_name}>
                          {product.finished_product_name}
                        </option>
                      ))}
                    </Input>
                  </Col>

                  {/* Machine No */}
                  <Col md={6}>
                    <Label>Machine No</Label>
                    <Input
                      type="select"
                      value={formData.machine_id}
                      onChange={(e) => setFormData({ ...formData, machine_id: e.target.value })}
                      required
                    >
                      <option value="">Select Machine No</option>
                      {machineList.map((machine) => (
                        <option key={machine.machine_id} value={machine.machine_id}>
                          {machine.machine_no} - {machine.machine_id}
                        </option>
                      ))}
                    </Input>
                  </Col>

                  {/* Stock Out Quantity */}
                  <Col md={6} className="mt-2">
                    <Label>Stock Out Quantity</Label>
                    <Input
                      placeholder="Enter Stock Out Quantity"
                      type="number"
                      min="1"
                      value={formData.stock_out}
                      onChange={(e) => setFormData({ ...formData, stock_out: parseFloat(e.target.value) || '' })}
                      required
                    />
                  </Col>

                  {/* UOM */}
                  <Col md={6} className="mt-2">
                    <Label>Unit of Measurement (UOM)</Label>
                    <Input
                      type="select"
                      value={formData.uom_id}
                      onChange={(e) => setFormData({ ...formData, uom_id: e.target.value })}
                      required
                    >
                      <option value="">Select UOM</option>
                      {uomOptions.map((uomOption) => (
                        <option key={uomOption.id} value={uomOption.id}>
                          {uomOption.name}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  
                  {/* Transaction Date */}
                  <Col md={6} className="mt-2">
                    <Label>Transaction Date</Label>
                    <Input
                      type="date"
                      value={formData.transaction_date}
                      onChange={(e) => setFormData({ ...formData, transaction_date: e.target.value })}
                      required
                    />
                  </Col>
                </Row>

                <Button className="mt-3" color="primary" onClick={handleSubmit} disabled={loading}>
                  {loading ? <Spinner size="sm" /> : 'Save Product'}
                </Button>
                {error && <div className="mt-3 text-danger">{error}</div>}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Page>
      <Footer />
    </div>
  );
};

export default MachineStockOut;
